import React, { useEffect, useState, useCallback } from 'react';
import { CollectionPointContext } from 'contexts';
import {
  getFranchiseCollectionPointChildren,
  getFranchiseCollectionPoints,
} from 'api/collectionPoints';
import { useQuery } from 'react-query';
import { useAuth } from 'hooks';
import { useReporting } from 'contexts/ReportingContext';

// Function to filter the entire tree and ensure no duplicates across siblings
const filterEntireTree = (collectionPoint) => {
  if (!collectionPoint) return collectionPoint;
  
  // Use a single global tracking set for the entire tree
  const globalDisplayedIds = new Set();
  
  const filterWithGlobalTracking = (cp) => {
    if (!cp) return cp;
    
    // Add current collection point to global tracking
    globalDisplayedIds.add(cp.id);
    
    // Process descendants first
    let updatedDescendants = [];
    if (cp.descendantCollectionPoints) {
      updatedDescendants = cp.descendantCollectionPoints.map(descendant => {
        return filterWithGlobalTracking(descendant);
      });
    }
    
    // Filter additional collection points using global tracking
    const originalAdditionalPoints = cp.additionalCollectionPoints || [];
    const filteredAdditionalPoints = originalAdditionalPoints.filter(
      additionalPoint => !globalDisplayedIds.has(additionalPoint.id)
    );
    
    // Add filtered additional points to global tracking
    filteredAdditionalPoints.forEach(point => globalDisplayedIds.add(point.id));
    
    return {
      ...cp,
      descendantCollectionPoints: updatedDescendants,
      additionalCollectionPoints: filteredAdditionalPoints
    };
  };
  
  return filterWithGlobalTracking(collectionPoint);
};

const CollectionPointProvider = ({ children }) => {
  const [collectionPoint, setCollectionPoint] = useState(null);
  const { redirectCollectionPointId } = useAuth();
  const { updateReportingParams } = useReporting();

  const collectionPointId = collectionPoint?.id || redirectCollectionPointId;

  const getAllCollectionPointIds = useCallback((collectionPoint) => {
    if (!collectionPoint) return [];
    const ids = [collectionPoint.id];
    if (collectionPoint.descendantCollectionPoints) {
      collectionPoint.descendantCollectionPoints.forEach(child => {
        ids.push(...getAllCollectionPointIds(child));
      });
    }
    return ids;
  }, []);

  const handleCollectionPointChange = useCallback((newCollectionPoint) => {
    setCollectionPoint(newCollectionPoint);
    
    // If the new collection point is the same as the current one, keep existing IDs
    if (newCollectionPoint?.id === collectionPoint?.id) {
      return;
    }

    updateReportingParams({
      allCollectionPointsIds: getAllCollectionPointIds(newCollectionPoint),
      collectionPointIds: newCollectionPoint?.id ? [newCollectionPoint.id] : [],
      additionalCollectionPointsIds: [],
      closestCommonParent: newCollectionPoint?.name,
    });
  }, [updateReportingParams, getAllCollectionPointIds, collectionPoint]);

  useQuery(
    ['firstFranchiseCollectionPoint'],
    () =>
      getFranchiseCollectionPoints({
        params: { 'page[size]': 1 },
      }),
    {
      onSuccess: data => {
        const filteredData = filterEntireTree(data?.data?.[0]);
        handleCollectionPointChange(filteredData);
      },
      enabled: !Boolean(collectionPointId),
    },
  );

  const { refetch } = useQuery(
    ['franchiseCollectionPointChildren', collectionPointId],
    () =>
      getFranchiseCollectionPointChildren({
        collectionPointId,
      }),
    {
      onSuccess: data => {
        const filteredData = filterEntireTree(data?.data);
        handleCollectionPointChange(filteredData);
      },
      enabled: Boolean(collectionPointId),
    },
  );

  useEffect(() => {
    if (collectionPointId) {
      refetch();
    }
  }, [collectionPointId, refetch]);

  return (
    <CollectionPointContext.Provider
      value={{
        collectionPoint,
        selectCollectionPoint: handleCollectionPointChange,
      }}
    >
      {children}
    </CollectionPointContext.Provider>
  );
};

export default CollectionPointProvider;
